<script setup>
import { ref } from 'vue';
import { Vue3SlideUpDown } from 'vue3-slide-up-down';
import useCartStore from '@vueApp/store/modules/cart.js';
import ITicket from '@components/ITicket/ITicket.vue';
import ILink from '@components/ILink/ILink.vue';
import ITeams from '@components/ITeams/ITeams.vue';
import { translator } from '@shop/frontend/web/js/services/TranslatorService';
import { utils } from '@frontend/web/src/js/utils';
import 'dayjs/locale/ru';
import dayjs from 'dayjs';

translator.getLanguages();

const lang = ref($('html').attr('lang') || 'ru');
dayjs.locale(lang.value);

const cartStore = useCartStore();
const props = defineProps({
  itemCase: {
    type: Object,
    required: true,
  },
  trashButton: {
    type: Boolean,
    default: true,
  },
});

const isEventsListOpen = ref(true);

function removeEventFromSubscription(subscriptionEventObject) {
  cartStore.removeEventFromSubscription(subscriptionEventObject, props.itemCase.orderId);
}

function removeOrder(orderId) {
  cartStore.removeOrderById(orderId);
}

function changeLoyalty(ticket, eventId, categoryId, priceId) {
  cartStore.changeLoyalty(ticket, eventId, props.itemCase.orderId, categoryId, priceId);
}
</script>

<template>
  <div class="i-order-subscribe-item__sub-wrapper">
    <a class="i-order-subscribe-item__sub-link" :href="'/subscription/' + itemCase.subscriptionId">
      {{ itemCase.name }}
    </a>
    <ILink v-if="trashButton" :icon-name="'trash'" class="trash-btn" @click="removeOrder(itemCase.orderId)">
      <span class="desktop">{{ translator.t('main', 'Remove') }}</span>
    </ILink>
  </div>
  <button class="i-order-subscribe-item__btn-events-open" @click="isEventsListOpen = !isEventsListOpen">
    {{ translator.t('main', 'Number of season tickets') }}:
    <strong class="mr-1">{{ itemCase.eventsCount }}</strong>
    <svg
      class="i-order-subscribe-item__arrow-svg"
      :class="{ 'i-order-event-item__arrow-svg_active': isEventsListOpen }"
    >
      <use xlink:href="#down" />
    </svg>
  </button>
  <vue3-slide-up-down v-model="isEventsListOpen" class="slider">
    <div v-for="(item, index) in itemCase.events" :key="index" class="i-order-subscribe-item__sub">
      <div class="i-order-subscribe-item__top-wrapper">
        <ITeams
          v-if="item.teams"
          :first-team="{
            ...item.teams[0],
            imageUrl: item.images.home_team,
          }"
          :second-team="{
            ...item.teams[1],
            imageUrl: item.images.guest_team,
          }"
        />
        <div v-else class="i-order-subscribe-item__sub-name">
          <div class="i-order-subscribe-item__sub-title">
            {{ item.name }}
          </div>
        </div>
        <div class="i-order-subscribe-item__top-right">
          <div class="d-flex">
            <div class="i-order-subscribe-item__details">
              <span class="time">
                <svg>
                  <use xlink:href="#calendar" />
                </svg>
                <span>
                  {{ dayjs(item.startTimestampStr).format("DD MMMM YYYY") }}
                </span>
                <span v-if="lang==='ru'" class="text-capitalize">
                  &nbsp;{{ dayjs(item.startTimestampStr).format("dd HH:mm") }}
                </span>
                <span v-else-if="lang==='en'" class="text-capitalize">
                  &nbsp;{{ dayjs(item.startTimestampStr).format("dd h:mm A") }}
                </span>
              </span>
              <span class="place">
                <svg>
                  <use xlink:href="#location" />
                </svg>
                {{ item.place }}
              </span>
            </div>
            <div class="desktop">
              {{ translator.t('main', 'Price') }}: <strong>{{ utils.formatNumber(item.summ) }} <span class="ruble">₽</span></strong>
            </div>
          </div>
          <ILink
            v-if="trashButton" :icon-name="'close'" class="close-btn" @click="
              removeEventFromSubscription({
                eventId: itemCase.subscriptionId,
                subEventId: index,
              })
            "
          />
        </div>
      </div>
      <div class="i-order-subscribe-item__tickets">
        <div v-for="ticket in item.carts" :key="ticket.place" class="tickets-item">
          <i-ticket
            :ticket="ticket" :inactive="true" :short="true" @change-loyalty="({ ticket, categoryId, priceId }) =>
              changeLoyalty(ticket, itemCase.id, categoryId, priceId)
            "
          />
        </div>
      </div>
      <div class="mobile">
        {{ translator.t('main', 'Price') }}: <strong>{{ item.summ }} ₽</strong>
      </div>
    </div>
  </vue3-slide-up-down>
</template>

<style scoped lang="scss">
.i-order-subscribe-item {
  &__sub-wrapper {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;

    .trash-btn {
      font-weight: 700;
      text-transform: uppercase;
    }
  }

  &__sub-link {
    font-size: 20px;
    font-weight: 700;
    color: $black;
  }

  &__btn-events-open {
    padding: 0;
    margin-bottom: 12px;
    background-color: inherit;
    border: none;
    font-size: 14px;
    color: $primary;
  }

  &__arrow-svg {
    width: 20px !important;
    height: 20px !important;
    stroke: $primary;

    &_active {
      transform: rotate(180deg);
    }
  }

  &__sub {
    position: relative;
    margin-bottom: 16px;
    padding: 20px;
    border: 1px solid $light-gray;
    border-radius: 16px;
  }

  &__top-wrapper {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  &__sub-title {
    margin-bottom: 10px;
    font-size: 20px;
    font-weight: 700;
  }

  &__top-right {
    display: flex;
    align-items: center;
    gap: 20px;

    .close-btn {
      margin-top: -2px;

      svg {
        margin-right: 0;
      }
    }
  }

  &__details {
    margin-bottom: 12px;
    display: flex;
    align-items: center;

    .time,
    .place {
      display: flex;
      align-items: center;
      font-weight: 400;

      svg {
        width: 16px;
        height: 16px;
        margin-right: 8px;
        margin-top: -1px;
      }
    }

    .time {
      margin-right: 24px;
      color: $primary;
      font-size: 16px;
    }

    .place {
      font-size: 12px;
      margin-right: 40px;
      max-width: 200px;
    }
  }

  &__tickets {
    width: 100%;
    display: flex;
    flex-wrap: wrap;

    .tickets-item {
      margin-right: 16px;
      margin-bottom: 8px;
    }
  }

  @media (max-width: $screen-md) {
    &__details {
      margin-bottom: 16px;
      align-items: flex-start;
      flex-direction: column;
      gap: 8px;
    }

    &__tickets {
      order: 2;
    }

    &__top-wrapper {
      display: flex;
      flex-direction: column;

      .i-teams {
        margin-bottom: 16px;
      }
    }

    &__top-right {
      width: 100%;

      .close-btn {
        margin-top: 0;
        position: absolute;
        top: 8px;
        right: 8px;

        svg {
          margin: 0;
        }
      }
    }

    .i-teams__info {
      margin-top: 30px;
    }
  }

  @media (max-width: $screen-small) {
    &__tickets {
      margin-bottom: 12px;

      .tickets-item {
        margin-right: 0px;
        width: 100%;
      }
    }

    &__btn-events-open {
      font-size: 14px;

      svg {
        margin-top: -1px;
      }
    }
  }
}
</style>
